/**
 * Account setup.
 *
 * Reached from the welcome screen, on a bare device and equally when an
 * existing driver starts a second, separate logbook — the two cases differ
 * only in the note on the account step.
 *
 * Five steps, of which two can be skipped. Language comes first because it
 * decides the wording of everything after it, and it has to run before any
 * account exists — so it lands in `localStorage` and is copied into the
 * encrypted settings as soon as there is a key to encrypt them with.
 *
 * The recovery code is shown exactly once and the user has to confirm they
 * have written it down. That friction is deliberate: there is no server, so
 * this code is the only path back into the data if the password is forgotten.
 *
 * Company and vehicle are asked here because an operator who fills them in on
 * day one never hits the "Firmendaten fehlen" wall halfway through their first
 * invoice — but a driver standing at the kerb can skip both and carry on.
 */

import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { assessPassword, createAccount, type AccountRecord } from '@/crypto/vault';
import { appendAudit } from '@/db/audit';
import { accountsDb, openAccountDb } from '@/db/schema';
import {
  saveAppSettings,
  saveCompanySettings,
  saveDriverIdentity,
  vehicleRepo,
} from '@/db/repositories';
import {
  DEFAULT_APP_SETTINGS,
  DEFAULT_COMPANY_SETTINGS,
  type CompanySettings,
} from '@/domain/types';
import { Callout, Field, Select, TextInput } from '@/components/ui';
import { LANGUAGE_NAMES, useLanguage, useT } from '@/i18n';

type Created = Awaited<ReturnType<typeof createAccount>>;

interface SetupScreenProps {
  onCreated: (account: Created) => void;
  /**
   * Back to the sign-in-or-register choice. Offered on the first two steps
   * only: from the recovery step on the account exists, and backing out would
   * strand it.
   */
  onCancel?: () => void;
}

type Step = 'language' | 'account' | 'recovery' | 'company' | 'vehicle';

const TOTAL_STEPS = 5;
const STEP_NUMBER: Record<Step, number> = {
  language: 1,
  account: 2,
  recovery: 3,
  company: 4,
  vehicle: 5,
};

export function SetupScreen({ onCreated, onCancel }: SetupScreenProps) {
  const [step, setStep] = useState<Step>('language');
  const [created, setCreated] = useState<Created | null>(null);

  return (
    <>
      {step === 'language' && (
        <LanguageStep onNext={() => setStep('account')} onCancel={onCancel} />
      )}

      {step === 'account' && (
        <AccountStep
          onCancel={onCancel}
          onCreated={(result) => {
            setCreated(result);
            setStep('recovery');
          }}
        />
      )}

      {step === 'recovery' && created && (
        <RecoveryStep created={created} onNext={() => setStep('company')} />
      )}

      {step === 'company' && created && (
        <CompanyStep created={created} onNext={() => setStep('vehicle')} />
      )}

      {step === 'vehicle' && created && (
        <VehicleStep
          created={created}
          onFinish={(account) => onCreated(account ? { ...created, account } : created)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

function LanguageStep({ onNext, onCancel }: { onNext: () => void; onCancel?: () => void }) {
  const t = useT();
  const { language, setLanguage } = useLanguage();

  return (
    <AuthShell
      title={t.setup.languageTitle}
      subtitle={t.setup.languageSubtitle}
      step={STEP_NUMBER.language}
    >
      <div className="space-y-2">
        {(['de', 'en'] as const).map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setLanguage(option)}
            aria-pressed={language === option}
            className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left text-sm transition-colors ${
              language === option
                ? 'border-accent bg-accent-soft text-heading font-medium'
                : 'border-line text-body hover:bg-subtle'
            }`}
          >
            {LANGUAGE_NAMES[option]}
            {language === option && <span className="text-accent">✓</span>}
          </button>
        ))}
      </div>

      <p className="text-muted mt-4 text-xs leading-relaxed">{t.setup.languageNote}</p>

      <button type="button" className="btn-primary mt-5 w-full" onClick={onNext}>
        {t.setup.continue}
      </button>

      {onCancel && (
        <button type="button" className="btn-ghost mt-2 w-full text-xs" onClick={() => onCancel()}>
          {t.welcome.back}
        </button>
      )}
    </AuthShell>
  );
}

/**
 * Turn a display name into a login name.
 *
 * The username is never shown during setup — one account on one phone does not
 * need one — but the vault is keyed by it, so it has to exist and be unique.
 */
export async function deriveUsername(displayName: string): Promise<string> {
  const base =
    displayName
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .trim()
      .split(/\s+/)
      .join('.')
      .replace(/[^a-z0-9.]/g, '') || 'fahrer';

  let candidate = base;
  for (
    let suffix = 2;
    await accountsDb.accounts.where('username').equals(candidate).count();
    suffix += 1
  ) {
    candidate = `${base}${suffix}`;
  }
  return candidate;
}

function AccountStep({
  onCreated,
  onCancel,
}: {
  onCreated: (created: Created) => void;
  onCancel?: () => void;
}) {
  const t = useT();
  const { language } = useLanguage();
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Warning about separate books is only meaningful next to an existing one. */
  const [isAdditional, setIsAdditional] = useState(false);

  useEffect(() => {
    void (async () => setIsAdditional((await accountsDb.accounts.count()) > 0))();
  }, []);

  const strength = assessPassword(password);
  const passwordsMatch = password !== '' && password === confirmation;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (!displayName.trim()) {
      setError(t.setup.nameRequired);
      return;
    }
    if (!passwordsMatch) {
      setError(t.setup.passwordsDiffer);
      return;
    }
    if (password.length < 12) {
      setError(t.setup.passwordTooShort);
      return;
    }

    setBusy(true);
    try {
      const result = await createAccount({
        username: await deriveUsername(displayName),
        displayName,
        // The real Fahrerkennung is asked for on the vehicle step; this is the
        // default so exports have something stable if that step is skipped.
        driverCode: 'F1',
        password,
      });
      await accountsDb.accounts.add(result.account);
      // A fresh, empty database for this driver's books. Everything below
      // writes into it, so it has to be in place before the first audit entry.
      openAccountDb(result.account.dbName);

      const ctx = { dataKey: result.dataKey, driverId: result.account.id };
      await appendAudit(ctx, {
        action: 'account.create',
        entityType: 'system',
        entityId: result.account.id,
        entityLabel: `Konto ${result.account.displayName} angelegt`,
        changes: [],
      });
      // Mirror the pre-login choice into the encrypted settings, so restoring
      // this backup on another device brings the language along.
      await saveAppSettings(ctx, { ...DEFAULT_APP_SETTINGS, language });

      onCreated(result);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t.setup.createFailed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      title={t.setup.accountTitle}
      subtitle={t.setup.accountSubtitle}
      step={STEP_NUMBER.account}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {isAdditional && <Callout tone="info">{t.setup.separateBooks}</Callout>}

        <Field label={t.setup.name} required>
          <TextInput
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            autoComplete="name"
            autoFocus
          />
        </Field>

        <Field
          label={t.setup.password}
          required
          hint={
            password
              ? t.setup.passwordStrength(strength.label, strength.issues[0])
              : t.setup.passwordHint
          }
        >
          <TextInput
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="new-password"
          />
        </Field>

        <Field
          label={t.setup.passwordRepeat}
          required
          error={confirmation && !passwordsMatch ? t.setup.mismatch : null}
        >
          <TextInput
            type="password"
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            autoComplete="new-password"
          />
        </Field>

        {error && <Callout tone="danger">{error}</Callout>}

        <button type="submit" className="btn-primary w-full" disabled={busy}>
          {busy ? t.setup.deriving : t.setup.createAccount}
        </button>

        {onCancel && (
          <button
            type="button"
            className="btn-ghost w-full text-xs"
            onClick={() => onCancel()}
            disabled={busy}
          >
            {t.welcome.back}
          </button>
        )}

        <p className="text-muted text-xs leading-relaxed">{t.setup.argonNote}</p>
      </form>
    </AuthShell>
  );
}

function RecoveryStep({ created, onNext }: { created: Created; onNext: () => void }) {
  const t = useT();
  const [acknowledged, setAcknowledged] = useState(false);

  return (
    <AuthShell
      title={t.setup.recoveryTitle}
      subtitle={t.setup.recoverySubtitle}
      step={STEP_NUMBER.recovery}
    >
      <Callout tone="warning" title={t.setup.recoveryWarningTitle}>
        {t.setup.recoveryWarningBody}
      </Callout>

      <div className="border-line-strong bg-subtle mt-5 rounded-xl border p-5 text-center">
        <p className="text-heading font-mono text-lg tracking-widest break-all">
          {created.recoveryCode}
        </p>
      </div>

      <div className="mt-4 flex gap-2">
        <button
          type="button"
          className="btn-secondary flex-1"
          onClick={() => void navigator.clipboard?.writeText(created.recoveryCode)}
        >
          {t.setup.copy}
        </button>
        <button
          type="button"
          className="btn-secondary flex-1"
          onClick={() => downloadRecoveryCode(created.account.username, created.recoveryCode)}
        >
          {t.setup.saveAsFile}
        </button>
      </div>

      <label className="text-body mt-5 flex cursor-pointer items-start gap-3 text-sm">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(event) => setAcknowledged(event.target.checked)}
          className="accent-accent mt-0.5 h-4 w-4"
        />
        <span>{t.setup.acknowledge}</span>
      </label>

      <p className="text-muted mt-3 text-xs">{t.setup.usernameNote(created.account.username)}</p>

      <button
        type="button"
        className="btn-primary mt-5 w-full"
        disabled={!acknowledged}
        onClick={onNext}
      >
        {t.setup.continue}
      </button>
    </AuthShell>
  );
}

function CompanyStep({ created, onNext }: { created: Created; onNext: () => void }) {
  const t = useT();
  const [company, setCompany] = useState<CompanySettings>(DEFAULT_COMPANY_SETTINGS);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function patch(changes: Partial<CompanySettings>) {
    setCompany((current) => ({ ...current, ...changes }));
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await saveCompanySettings(
        { dataKey: created.dataKey, driverId: created.account.id },
        company,
      );
      onNext();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t.common.saveFailed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      title={t.setup.companyTitle}
      subtitle={t.setup.companySubtitle}
      step={STEP_NUMBER.company}
    >
      <div className="space-y-4">
        <Field label={t.settings.companyName}>
          <TextInput
            value={company.companyName}
            onChange={(event) => patch({ companyName: event.target.value })}
            autoFocus
          />
        </Field>
        <Field label={t.settings.street}>
          <TextInput
            value={company.street}
            onChange={(event) => patch({ street: event.target.value })}
          />
        </Field>
        <div className="grid grid-cols-[100px_minmax(0,1fr)] gap-3">
          <Field label={t.settings.postalCode}>
            <TextInput
              value={company.postalCode}
              onChange={(event) => patch({ postalCode: event.target.value })}
              inputMode="numeric"
            />
          </Field>
          <Field label={t.settings.city}>
            <TextInput
              value={company.city}
              onChange={(event) => patch({ city: event.target.value })}
            />
          </Field>
        </div>
        <Field label={t.settings.taxNumber} hint={t.settings.taxNumberHint}>
          <TextInput
            value={company.taxNumber ?? ''}
            onChange={(event) => patch({ taxNumber: event.target.value || undefined })}
          />
        </Field>
        <Field label={t.settings.licenceType}>
          <Select
            value={company.licenceType}
            onChange={(event) =>
              patch({ licenceType: event.target.value as CompanySettings['licenceType'] })
            }
          >
            <option value="mietwagen">{t.settings.licenceMietwagen}</option>
            <option value="taxi">{t.settings.licenceTaxi}</option>
          </Select>
        </Field>
        <Field label={t.settings.kleinunternehmer}>
          <Select
            value={company.kleinunternehmer ? 'ja' : 'nein'}
            onChange={(event) => patch({ kleinunternehmer: event.target.value === 'ja' })}
          >
            <option value="nein">{t.settings.kleinunternehmerNo}</option>
            <option value="ja">{t.settings.kleinunternehmerYes}</option>
          </Select>
        </Field>
      </div>

      <p className="text-muted mt-4 text-xs leading-relaxed">{t.setup.companyNote}</p>

      {error && <Callout tone="danger">{error}</Callout>}

      <StepFooter
        busy={busy}
        onSkip={onNext}
        onContinue={() => void save()}
        continueLabel={t.setup.continue}
      />
    </AuthShell>
  );
}

function VehicleStep({
  created,
  onFinish,
}: {
  created: Created;
  /** Passes the account back if the Fahrerkennung changed. */
  onFinish: (account?: AccountRecord) => void;
}) {
  const t = useT();
  const [licensePlate, setLicensePlate] = useState('');
  const [vehicleCode, setVehicleCode] = useState('01');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [driverCode, setDriverCode] = useState(created.account.driverCode);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const ctx = { dataKey: created.dataKey, driverId: created.account.id };

      if (licensePlate.trim()) {
        await vehicleRepo.create(ctx, {
          id: crypto.randomUUID(),
          vehicleCode: vehicleCode.trim() || '01',
          licensePlate: licensePlate.trim().toUpperCase(),
          make: make.trim(),
          model: model.trim(),
          active: true,
          createdAt: new Date().toISOString(),
        });
      }

      const renamed =
        driverCode.trim() && driverCode.trim() !== created.account.driverCode
          ? await saveDriverIdentity(ctx, created.account.id, {
              displayName: created.account.displayName,
              driverCode,
            })
          : undefined;

      onFinish(renamed);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t.settings.createFailed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      title={t.setup.vehicleTitle}
      subtitle={t.setup.vehicleSubtitle}
      step={STEP_NUMBER.vehicle}
    >
      <div className="space-y-4">
        <Field label={t.settings.licensePlate}>
          <TextInput
            value={licensePlate}
            onChange={(event) => setLicensePlate(event.target.value)}
            placeholder="M-XY 1234"
            autoFocus
          />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t.settings.make}>
            <TextInput value={make} onChange={(event) => setMake(event.target.value)} />
          </Field>
          <Field label={t.settings.model}>
            <TextInput value={model} onChange={(event) => setModel(event.target.value)} />
          </Field>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t.settings.vehicleCode} hint={t.settings.vehicleCodeHint}>
            <TextInput
              value={vehicleCode}
              onChange={(event) => setVehicleCode(event.target.value)}
            />
          </Field>
          <Field label={t.settings.driverCode} hint={t.settings.driverCodeHint}>
            <TextInput
              value={driverCode}
              onChange={(event) => setDriverCode(event.target.value)}
            />
          </Field>
        </div>
      </div>

      <p className="text-muted mt-4 text-xs leading-relaxed">{t.setup.vehicleNote}</p>

      {error && <Callout tone="danger">{error}</Callout>}

      <StepFooter
        busy={busy}
        onSkip={() => onFinish()}
        onContinue={() => void save()}
        continueLabel={t.setup.finish}
      />
    </AuthShell>
  );
}

function StepFooter({
  busy,
  onSkip,
  onContinue,
  continueLabel,
}: {
  busy: boolean;
  onSkip: () => void;
  onContinue: () => void;
  continueLabel: string;
}) {
  const t = useT();
  return (
    <>
      <div className="mt-5 flex gap-2">
        <button
          type="button"
          className="btn-secondary flex-1"
          onClick={() => onSkip()}
          disabled={busy}
        >
          {t.setup.skip}
        </button>
        <button type="button" className="btn-primary flex-1" onClick={onContinue} disabled={busy}>
          {busy ? t.common.saving : continueLabel}
        </button>
      </div>
      <p className="text-muted mt-2 text-center text-xs">{t.setup.skipNote}</p>
    </>
  );
}

function downloadRecoveryCode(username: string, code: string): void {
  const content = [
    'Fahrtenbuch Pro — Wiederherstellungscode',
    '',
    `Benutzer: ${username}`,
    `Code:     ${code}`,
    `Erstellt: ${new Date().toLocaleString('de-DE')}`,
    '',
    'Mit diesem Code kann das Passwort zurückgesetzt und auf die',
    'verschlüsselten Daten zugegriffen werden. Bewahren Sie ihn getrennt',
    'vom Gerät auf.',
    '',
  ].join('\n');

  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `fahrtenbuch-wiederherstellungscode-${username}.txt`;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function AuthShell({
  title,
  subtitle,
  step,
  children,
}: {
  title: string;
  subtitle?: string;
  /** Shows the wizard progress; omitted outside setup. */
  step?: number;
  children: ReactNode;
}) {
  const t = useT();

  return (
    <div className="flex min-h-dvh items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="bg-primary mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl text-xl font-bold text-white">
            FB
          </div>
          {step !== undefined && (
            <p className="text-muted mb-2 text-xs font-medium tracking-wide uppercase">
              {t.setup.stepOf(step, TOTAL_STEPS)}
            </p>
          )}
          <h1 className="text-heading text-2xl font-semibold">{title}</h1>
          {subtitle && <p className="text-muted mt-2 text-sm">{subtitle}</p>}
        </div>
        <div className="card">{children}</div>
      </div>
    </div>
  );
}
